import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onLogout = () => { 
    logout(); 
    nav("/"); 
    setLogoutConfirm(false);
  };

  const links = [
    { to: "/", label: "Home" },
    { to: "/invest", label: "Plans" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header className="sticky top-0 z-40 glass border-b border-white/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" onClick={scrollTop} className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-10 h-10 rounded-md bg-[#D4AF37] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#0A0E27]" />
          </div>
          <div className="leading-none">
            <div className="font-display text-2xl font-black tracking-tight">Duo<span className="gold-text">capital</span></div>
            <div className="text-[10px] tracking-[0.25em] text-white/40 uppercase">Invest. Together.</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={scrollTop} className="text-sm tracking-wide text-white/70 hover:text-[#D4AF37] transition-colors" data-testid={`nav-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin-duo-secure" : "/dashboard"} className="text-sm text-white/80 hover:text-white" data-testid="nav-dashboard">
                {user.role === "admin" ? "Admin" : "Dashboard"}
              </Link>
              <button onClick={() => setLogoutConfirm(true)} className="btn-outline" data-testid="nav-logout">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-white/80 hover:text-white" data-testid="nav-login">Login</Link>
              <Link to="/register" className="btn-primary" data-testid="nav-register">Start Investing</Link>
            </>
          )}
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass border-t border-white/5 px-6 py-6 space-y-4" data-testid="nav-mobile-menu">
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={() => { scrollTop(); setOpen(false); }} className="block text-white/80" data-testid={`mnav-${l.label.toLowerCase()}`}>{l.label}</Link>
          ))}
          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin-duo-secure" : "/dashboard"} onClick={() => { scrollTop(); setOpen(false); }} className="block text-white/80">Dashboard</Link>
              <button onClick={() => { setOpen(false); setLogoutConfirm(true); }} className="btn-outline w-full">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => { scrollTop(); setOpen(false); }} className="block text-white/80">Login</Link>
              <Link to="/register" onClick={() => { scrollTop(); setOpen(false); }} className="btn-primary block text-center">Start Investing</Link>
            </>
          )}
        </div>
      )}

      <AlertDialog open={logoutConfirm} onOpenChange={setLogoutConfirm}>
        <AlertDialogContent className="bg-[#0A0E27] border border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Sign out</AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              Are you sure you want to log out? You'll need to sign in again to access your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3">
            <AlertDialogCancel className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="logout-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={onLogout} className="bg-red-600 hover:bg-red-700" data-testid="logout-confirm">
              Sign Out
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
